import { Component, ElementRef, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { Router, ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize } from 'rxjs/operators';
import { of, Subscription } from 'rxjs';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { GetQuizByAttemptIdResponse } from '@edtech-ui/core/models/interfaces/attempt-Quiz/quiz-Question';
import { ScrollQuestionInStudentResult } from '@edtech-ui/constants/scroll-page.enum';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MathLatexComponent } from '@edtech-ui/shared/latex/math-latex/math-latex.component';
import { QuestionBlocksComponent } from '@edtech-ui/shared/question-blocks/question-blocks.component';

interface QuestionStatus {
	isCorrect: boolean;
	isIncorrect: boolean;
	isSkipped: boolean;
	isPartial: boolean;
}

@Component({
	selector: 'app-detailed-solutions',
	standalone: true,
	imports: [CommonModule, MatButtonModule, MatProgressSpinnerModule, MatTooltipModule, MathLatexComponent, QuestionBlocksComponent],
	templateUrl: './detailed-solutions.component.html',
	styleUrls: ['./detailed-solutions.component.scss']
})
export class DetailedSolutionsComponent implements OnInit, OnDestroy {
	@ViewChild('questionsContainer') questionsContainer!: ElementRef;
	@ViewChild('questionNav') questionNav!: ElementRef;
	selectedQuestionIndex: number | null = null;
	questions: any[] = [];
	quizName: string = '';
	isLoading: boolean = false;
	attemptId: string | null = null;
	classroomId: string | null = null;
	strong_areas: string[] = [];
	weakAreas: string[] = [];
	ScrollQuestionInStudentResult = ScrollQuestionInStudentResult;
	subscription = new Subscription();
	courseId!: string;

	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private quizService: QuizManagementService,
		private toastr: ToastrService
	) { }

	ngOnInit() {
		this.attemptId = this.route.snapshot.paramMap.get('attemptId');
		this.classroomId = this.route.snapshot.parent?.paramMap.get('id') || null;
		this.courseId = this.route.parent?.snapshot.paramMap.get('courseId')!;

		if (this.attemptId) {
			this.fetchQuizResults(this.attemptId);
		} else {
			this.toastr.error('No attempt ID provided.', 'Error', { timeOut: 3000 });
			this.navigateBack();
		}
	}

	fetchQuizResults(attemptId: string) {
		this.isLoading = true;
		this.subscription.add(
			this.quizService.getQuizByAttemptId(attemptId).pipe(
				catchError((error) => {
					this.toastr.error('Failed to load quiz results. Please try again.', 'Error', { timeOut: 3000 });
					return of(null);
				}),
				finalize(() => (this.isLoading = false))
			).subscribe((response: GetQuizByAttemptIdResponse | null) => {
				if (response?.Success && response.Data) {
					this.quizName = response.Data.quiz_name;
					this.classroomId = response.Data.classroom_id.toString() || this.classroomId;
					this.questions = response.Data.quiz_conversation.map((conv, index: number) => ({
						id: index + 1,
						text: conv.questions.question_text,
						question_blocks: conv.questions.question_blocks || null,
						diagrams: conv.questions.diagrams || [],
						options: conv.questions.options?.map(opt => ({
							text: opt.text,
							blocks: opt.blocks || null
						})) || (conv.questions.question_type === 'TRUE_FALSE'
							? [{ text: 'True', blocks: null }, { text: 'False', blocks: null }]
							: []),
						correctAnswer: conv.questions.correct_answer,
						userAnswer: conv.user_answer,
						points: conv.questions.marks,
						feedback: conv.evaluation.feedback,
						review: conv.evaluation.review,
						questionType: conv.questions.question_type,
						answerType: conv.evaluation.answer_type,
						obtainedMarks: conv.evaluation.obtained_marks,
						isSkipped: conv.is_skipped
					}));
					this.strong_areas = response.Data.overall_user_performance.strong_areas || [];
					this.weakAreas = response.Data.overall_user_performance.areas_for_improvement || [];
				} else {
					this.toastr.error('No quiz data found.', 'Error', { timeOut: 3000 });
					this.questions = [];
					this.strong_areas = [];
					this.weakAreas = [];
				}
			}));
	}

	navigateBack() {
		this.router.navigate(['student-dashboard/course-detail-page', this.classroomId, this.courseId, 'quiz-list']);
	}


	getQuestionStatus(index: number): QuestionStatus {
		const q = this.questions[index];
		const isSkipped = q?.isSkipped || false;
		const isCorrect = isSkipped ? false : (q?.questionType === 'OPEN_ENDED' ? q?.answerType === 'correct' : q?.userAnswer === q?.correctAnswer);
		const isPartial = q?.answerType === 'partial';
		const isIncorrect = !isCorrect && !isSkipped && !isPartial;

		return {
			isCorrect,
			isIncorrect,
			isSkipped,
			isPartial
		};
	}

	getAnswerStatus(index: number): string {
		const status = this.getQuestionStatus(index);
		if (status.isSkipped) {
			return 'Not Answered';
		}
		return status.isCorrect ? 'Correct' : 'Incorrect';
	}

	private getOptionText(option: string | { text: string }): string {
		return typeof option === 'string' ? option : option.text;
	}

	isUserAnswer(index: number, option: string | { text: string }): boolean {
		return this.questions[index]?.userAnswer === this.getOptionText(option);
	}

	isCorrectAnswer(index: number, option: string | { text: string }): boolean {
		return this.questions[index]?.correctAnswer === this.getOptionText(option);
	}

	isCorrectOption(index: number, option: string | { text: string }): boolean {
		return this.isCorrectAnswer(index, option);
	}

	isUserCorrect(index: number, option: string | { text: string }): boolean {
		return this.isUserAnswer(index, option) && this.getQuestionStatus(index).isCorrect;
	}

	isUserIncorrect(index: number, option: string | { text: string }): boolean {
		return this.isUserAnswer(index, option) && !this.getQuestionStatus(index).isCorrect;
	}

	getUserAnswerText(index: number): string {
		const q = this.questions[index];
		return q?.isSkipped ? 'Question skipped' : (q?.userAnswer || 'No answer provided');
	}

	getCorrectAnswerText(index: number): string {
		return this.questions[index]?.correctAnswer || 'Not provided';
	}

	getExplanation(index: number): string {
		const q = this.questions[index];
		if (q?.isSkipped) {
			return q?.review || 'Question was skipped.';
		}
		if (q?.review) return q.review;
		return this.getQuestionStatus(index).isCorrect
			? 'Correct!'
			: `${q?.correctAnswer || 'the correct answer'} is the correct answer, not ${q?.userAnswer || 'your answer'}.`;
	}

	getFeedback(index: number): string {
		const q = this.questions[index];
		if (q?.isSkipped) {
			return q?.feedback || 'You skipped this question. Try answering all questions next time.';
		}
		if (q?.feedback) return q.feedback;
		return this.getQuestionStatus(index).isCorrect
			? 'Excellent work! You have a solid understanding of this concept.'
			: 'This needs improvement. Review the topic to understand the correct answer.';
	}

	scrollToQuestion(index: number) {
		this.selectedQuestionIndex = index;
		const questionElement = this.questionsContainer.nativeElement.querySelector(`#question-${index}`);
		if (questionElement) {
			questionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	}

	scrollQuestions(direction: string) {
		const container = this.questionNav.nativeElement;
		const scrollAmount = 200;
		if (direction === 'left') {
			container.scrollLeft -= scrollAmount;
		} else {
			container.scrollLeft += scrollAmount;
		}
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
}